import { PropertyType } from '~/client/graph/types.generated'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { parseDateValue } from '~/client/src/shared/utils/dateValueParser'
import { UNASSIGNED_FILTER_VALUE } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import { IResourceFieldCatalog } from '../catalog/types'
import {
  getResourceFieldDefinition,
  IResourceRuntimeContext,
} from '../definitions'
import { IResourceRecord } from '../definitions/types'
import { createResourceDisplayValue } from '../presentation/createResourceDisplayValue'
import { resolveResourceValue } from '../resolveResourceValue'
import { sortRecordsBySortKey } from './sortRecordsBySortKey'
import {
  IResolvedResourceFieldProjection,
  IResourceFieldProjector,
  IResourceProjectionEntry,
  ResourceSortKey,
} from './types'

interface ICreateResourceFieldProjectorOptions {
  catalog: Pick<IResourceFieldCatalog, 'fields' | 'getField'>
  resourceTypeId?: string
  runtimeContext: IResourceRuntimeContext
  projectDateStore?: ProjectDateStore
  projectMembersStore?: ProjectMembersStore
  companiesStore?: CompaniesStore
  tagsStore?: TagsStore
}

const buildSearchText = (...values: Array<string | undefined>): string => {
  const uniqueValues = Array.from(
    new Set(
      values
        .map(value => value?.trim())
        .filter((value): value is string => !!value),
    ),
  )

  return uniqueValues.join(' ')
}

const isEmptyRawValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0)

const toDateSortValue = (
  value: unknown,
  projectDateStore?: ProjectDateStore,
): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const numeric = Number(value)
    if (Number.isFinite(numeric)) {
      return numeric
    }

    const parsed = Date.parse(value)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  const parsedDate = parseDateValue(value, projectDateStore)
  return parsedDate ? parsedDate.getTime() : null
}

const resolveSortValue = ({
  rawValue,
  displayText,
  propertyType,
  projectDateStore,
}: {
  rawValue: unknown
  displayText: string
  propertyType?: PropertyType | string
  projectDateStore?: ProjectDateStore
}): string | number | null => {
  if (isEmptyRawValue(rawValue)) {
    return null
  }

  if (propertyType === PropertyType.Number) {
    const numeric = Number(rawValue)
    return Number.isFinite(numeric) ? numeric : null
  }

  if (propertyType === PropertyType.Datetime) {
    return toDateSortValue(rawValue, projectDateStore)
  }

  if (propertyType === PropertyType.Checkbox) {
    return rawValue === true || rawValue === 'true' ? 1 : 0
  }

  return displayText.toLocaleLowerCase()
}

const resolveRawValue = ({
  record,
  resourceTypeId,
  key,
}: {
  record: IResourceRecord
  resourceTypeId?: string
  key: string
}): unknown => {
  if (key === 'name') {
    return record.name
  }

  if (key === 'stxResourceId') {
    return record.stxResourceId
  }

  return resolveResourceValue({
    record,
    resourceTypeId,
    key,
  })
}

export const createResourceFieldProjector = ({
  catalog,
  resourceTypeId,
  runtimeContext,
  projectDateStore,
  projectMembersStore,
  companiesStore,
  tagsStore,
}: ICreateResourceFieldProjectorOptions): IResourceFieldProjector => {
  const displayProperties = catalog.fields.map(field => ({
    name: field.key,
    propertyType: field.propertyType,
    configuration: {
      options: field.configuration?.options,
      unit: field.configuration?.unit,
      resourceLink: field.configuration?.resourceLink,
    },
  }))

  const getDisplayValue = createResourceDisplayValue({
    properties: displayProperties,
    resourceTypeId,
    runtimeContext,
    projectDateStore,
    projectMembersStore,
    companiesStore,
    tagsStore,
  })

  const createEntry = (
    key: string,
    value: unknown,
  ): IResourceProjectionEntry | null => {
    if (isEmptyRawValue(value)) {
      return null
    }

    const fieldDefinition = getResourceFieldDefinition(resourceTypeId, key)
    const semanticValue = fieldDefinition?.table?.semanticValueFn
      ? fieldDefinition.table.semanticValueFn(value, runtimeContext)
      : String(value)
    const displayText = getDisplayValue(key, value)
    const label = fieldDefinition?.table?.semanticLabelFn
      ? fieldDefinition.table.semanticLabelFn(semanticValue, runtimeContext)
      : displayText

    return {
      id: semanticValue,
      label,
    }
  }

  const createUnassignedEntry = (): IResourceProjectionEntry => ({
    id: UNASSIGNED_FILTER_VALUE,
    label: Localization.translator.unassigned,
  })

  const buildEntries = (
    key: string,
    rawValue: unknown,
  ): IResourceProjectionEntry[] =>
    Array.from(
      new Map(
        (Array.isArray(rawValue) ? rawValue : [rawValue])
          .map(value => createEntry(key, value))
          .filter((entry): entry is IResourceProjectionEntry => !!entry)
          .map(entry => [entry.id, entry]),
      ).values(),
    )

  const resolveSortKey = ({
    rawValue,
    displayText,
    entries,
    descriptor,
  }: {
    rawValue: unknown
    displayText: string
    entries: IResourceProjectionEntry[]
    descriptor: ReturnType<typeof catalog.getField>
  }): ResourceSortKey => {
    const base = resolveSortValue({
      rawValue,
      displayText,
      propertyType: descriptor?.propertyType,
      projectDateStore,
    })

    const hierarchy = descriptor?.hierarchy
    if (!hierarchy) return base

    const sortPath = entries.flatMap(entry => hierarchy.getLabelPath(entry.id))

    return sortPath.length ? sortPath : base
  }

  const fromValue = (
    key: string,
    rawValue: unknown,
  ): IResolvedResourceFieldProjection => {
    const descriptor = catalog.getField(key)
    const isEmpty = isEmptyRawValue(rawValue)
    const displayText = getDisplayValue(key, rawValue)

    if (isEmpty) {
      const unassignedEntry = createUnassignedEntry()
      return {
        key,
        rawValue,
        isEmpty,
        displayText,
        searchText: '',
        filterEntries: [unassignedEntry],
        groupEntries: [unassignedEntry],
      }
    }

    const entries = buildEntries(key, rawValue)

    const hierarchy = descriptor?.hierarchy

    let filterEntries = entries

    if (hierarchy) {
      filterEntries = hierarchy.expandToFilterEntries(entries)
    }

    return {
      key,
      rawValue,
      isEmpty,
      displayText,
      searchText: buildSearchText(
        displayText,
        ...entries.map(entry => entry.label),
      ),
      filterEntries,
      groupEntries: entries,
    }
  }

  const fromEntryId = (key: string, id: string): IResourceProjectionEntry => {
    if (id === UNASSIGNED_FILTER_VALUE) {
      return createUnassignedEntry()
    }

    const fieldDefinition = getResourceFieldDefinition(resourceTypeId, key)
    const label = fieldDefinition?.table?.semanticLabelFn
      ? fieldDefinition.table.semanticLabelFn(id, runtimeContext)
      : getDisplayValue(key, id)

    return {
      id,
      label,
    }
  }

  const resolve = (record: IResourceRecord, key: string) =>
    fromValue(
      key,
      resolveRawValue({
        record,
        resourceTypeId,
        key,
      }),
    )

  const resolveSortKeyForRecord = (
    record: IResourceRecord,
    key: string,
  ): ResourceSortKey => {
    const rawValue = resolveRawValue({ record, resourceTypeId, key })
    if (isEmptyRawValue(rawValue)) return null

    const descriptor = catalog.getField(key)
    const displayText = getDisplayValue(key, rawValue)
    const entries = buildEntries(key, rawValue)

    return resolveSortKey({ rawValue, displayText, entries, descriptor })
  }

  const sortRecords = <T extends IResourceRecord>(
    records: T[],
    key: string,
    order: SortOrder,
  ): T[] =>
    sortRecordsBySortKey(
      records,
      record => resolveSortKeyForRecord(record, key),
      order,
    )

  return {
    runtimeContext,
    resolve,
    fromValue,
    fromEntryId,
    sortRecords,
    getSortKey: resolveSortKeyForRecord,
  }
}
